import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Layers,
  Receipt,
  UserPlus,
  Users,
} from "lucide-react";
import {
  CUSTOMER_TYPE_LABELS,
  buildReferralLink,
  formatReferralCode,
  type DownlineMember,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState } from "@/shared/ui/ui";
import { DataTableFrame, FilterBar, KpiCard } from "@/features/admin/components";
import { MemberActivity } from "./MemberActivity";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useAuth } from "@/features/auth/auth.context";
import { InviteQr } from "@/features/network/InviteQr";
import { useEnrollInNetwork, useMyDownline, useMyNetworkNode } from "./minegocio.api";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Mi red — lo RELEVANTE de los "invitados" del sistema viejo
 * (`dashboard/invitados`), con el diseño de esta versión (Anahí,
 * 27-ago):
 *
 *  · El enlace de invitación con Copiar y WhatsApp — el motor de todo.
 *  · Los números: cuántos en la red, cuántos directos, a qué nivel.
 *  · Buscador y filtro de nivel sobre la línea.
 *  · El ÁRBOL expandible (quién invitó a quién, con sus conectores);
 *    al buscar o filtrar, la lista plana de coincidencias.
 *  · Descargar Excel (CSV) con la red completa, como allá.
 *
 * Lo que NO se portó, a propósito: los % de comisión por profundidad
 * (allá mismo estaban anotados como falsos) y el QR — el enlace por
 * WhatsApp es el camino que la gente usa de verdad.
 * ═══════════════════════════════════════════════════════════════════
 */
export function RedPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const node = useMyNetworkNode();
  const enroll = useEnrollInNetwork();
  const downline = useMyDownline();

  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const inviteUrl = node.data
    ? buildReferralLink(node.data.referralCode, window.location.origin)
    : null;

  const items = useMemo(() => downline.data?.items ?? [], [downline.data]);
  const total = downline.data?.total ?? 0;
  const counts = downline.data?.countsByLevel ?? {};
  const directos = counts["1"] ?? 0;
  const nivelesActivos = Object.entries(counts).filter(([, n]) => n > 0).length;
  const levels = Object.keys(counts)
    .filter((key) => (counts[key] ?? 0) > 0)
    .sort((a, b) => Number(a) - Number(b));

  /** El árbol: hijos por patrocinador. La raíz es quien mira. */
  const childrenOf = useMemo(() => {
    const map = new Map<string, DownlineMember[]>();
    for (const member of items) {
      const parent = member.sponsorId ?? "";
      map.set(parent, [...(map.get(parent) ?? []), member]);
    }
    return map;
  }, [items]);

  const filtering = search.trim() !== "" || levelFilter !== "";
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((member) => {
      const inLevel = !levelFilter || String(member.level) === levelFilter;
      const inSearch =
        !term ||
        member.displayName.toLowerCase().includes(term) ||
        member.referralCode.toLowerCase().includes(term.replace(/-/g, ""));
      return inLevel && inSearch;
    });
  }, [items, search, levelFilter]);

  function toggle(userId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  /** El CSV de la red — mismas columnas que el Excel del viejo. */
  function descargarExcel() {
    const nameOf = new Map(items.map((member) => [member.userId, member.displayName]));
    const rows = [
      ["Nombre", "Código", "Nivel", "Invitado por", "Tipo", "Entró el"],
      ...items.map((member) => [
        member.displayName,
        formatReferralCode(member.referralCode),
        String(member.level),
        member.sponsorId === user?.id
          ? t("Tú")
          : (nameOf.get(member.sponsorId ?? "") ?? "—"),
        t(CUSTOMER_TYPE_LABELS[member.customerType]),
        formatOrderDate(member.enrolledAt),
      ]),
    ];
    const csv =
      "﻿" +
      rows.map((row) => row.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `mi-red-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function renderBranch(parentId: string, depth: number): ReactElement[] {
    return (childrenOf.get(parentId) ?? []).map((member) => {
      const kids = childrenOf.get(member.userId) ?? [];
      const open = !collapsed.has(member.userId);
      return (
        <li key={member.userId}>
          <MemberRow
            member={member}
            depth={depth}
            hasKids={kids.length > 0}
            open={open}
            onToggle={() => toggle(member.userId)}
          />
          {kids.length > 0 && open && (
            <ul className="relative">{renderBranch(member.userId, depth + 1)}</ul>
          )}
        </li>
      );
    });
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Mi red")}
        description={t("Tu enlace de invitación y la gente de tu línea, rama por rama.")}
      />

      {/* ── El enlace: el motor de todo lo demás ─────────────────────── */}
      <section className="relative mb-4 rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
        <div className="relative grid gap-3">
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
            <UserPlus className="h-4 w-4 text-ds-violet-pop" />
            {t("Tu enlace de invitación")}
          </h2>

          {node.isPending ? (
            <p className="text-[13px] text-ds-ink-3">{t("Cargando…")}</p>
          ) : node.data ? (
            <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
              <div className="grid min-w-0 content-start gap-2">
              <p className="font-mono text-[24px] font-semibold tracking-wider text-ds-ink">
                {formatReferralCode(node.data.referralCode)}
              </p>
              <p className="min-w-0 break-all font-mono text-[12px] text-ds-ink-3">{inviteUrl}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={copied ? <Check /> : <Copy />}
                  onClick={() => {
                    if (!inviteUrl) return;
                    void navigator.clipboard?.writeText(inviteUrl);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? t("Copiado") : t("Copiar")}
                </Button>
                <Button
                  size="sm"
                  nav
                  onClick={() =>
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(
                        t("Únete a mi equipo en It's The Best: {{url}}", { url: inviteUrl ?? "" }),
                      )}`,
                      "_blank",
                      "noreferrer",
                    )
                  }
                >
                  {t("Invitar por WhatsApp")}
                </Button>
              </div>
              </div>
              {/* El QR con sus acciones, a la DERECHA: descargable,
                  compartible, o en grande para que lo escaneen. */}
              {inviteUrl && (
                <InviteQr
                  url={inviteUrl}
                  fileName="mi-invitacion-its-the-best"
                  shareText={t("Únete a mi equipo en It's The Best:")}
                />
              )}
            </div>
          ) : (
            <div className="grid gap-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[14px] leading-relaxed text-ds-ink-3">
                  {t("Tu cuenta todavía no está en la red. Activa tu código para empezar a invitar.")}
                </p>
                <Button loading={enroll.isPending} onClick={() => enroll.mutate()}>
                  {t("Activar mi código")}
                </Button>
              </div>
              {enroll.isError && (
                <Alert variant="error" onDismiss={() => enroll.reset()}>
                  {t("No se pudo activar tu código. Inténtalo otra vez.")}
                </Alert>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Los números de la red ────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        <KpiCard icon={Users} item="mi-red" label={t("En tu red")} value={String(total)} hint={t("hasta 8 niveles")} />
        <KpiCard icon={UserPlus} label={t("Directos")} value={String(directos)} />
        <KpiCard icon={Layers} label={t("Niveles activos")} value={String(nivelesActivos)} />
      </div>

      {/* ── La línea: árbol expandible; filtrada, lista plana ─────────── */}
      <DataTableFrame>
        <FilterBar
          actions={
            items.length > 0 && (
              <Button variant="secondary" size="sm" leftIcon={<Download />} onClick={descargarExcel}>
                {t("Descargar Excel")}
              </Button>
            )
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="w-56">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("Nombre o código…")}
              />
            </div>
          </label>
          {levels.length > 1 && (
            <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
              {t("Nivel")}
              <div className="w-40">
                <Select
                  aria-label={t("Nivel")}
                  value={levelFilter}
                  onChange={(event) => setLevelFilter(event.target.value)}
                  placeholder={t("Todos")}
                  options={levels.map((value) => ({
                    value,
                    label: t("Nivel {{n}} ({{c}})", { n: value, c: counts[value] }),
                  }))}
                />
              </div>
            </label>
          )}
        </FilterBar>

        {downline.isPending || !node.data ? (
          <div className="p-4">
            <EmptyState
              icon={<Users className="h-5 w-5 text-ds-ink-3" />}
              title={t("Tus invitados")}
              description={
                node.data
                  ? t("Cargando tu línea…")
                  : t("Con tu código activo, aquí verás a quien entre por tu enlace.")
              }
            />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<Users className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía no hay invitados")}
              description={t("Comparte tu enlace: quien se registre con él aparece aquí.")}
            />
          </div>
        ) : filtering ? (
          filtered.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<Users className="h-5 w-5 text-ds-ink-3" />}
                title={t("Sin resultados")}
                description={t("Nada coincide con ese filtro.")}
              />
            </div>
          ) : (
            <ul className="p-3">
              {filtered.map((member) => (
                <li key={member.userId}>
                  <MemberRow member={member} depth={0} hasKids={false} open onToggle={() => {}} />
                </li>
              ))}
            </ul>
          )
        ) : (
          <ul className="p-3">{renderBranch(user?.id ?? "", 0)}</ul>
        )}
      </DataTableFrame>
    </>
  );
}

/** Una persona de la línea: avatar, nombre, sus chips y su rama. */
function MemberRow({
  member,
  depth,
  hasKids,
  open,
  onToggle,
}: {
  member: DownlineMember;
  depth: number;
  hasKids: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const [showActivity, setShowActivity] = useState(false);
  const initials = member.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <>
    <div
      className="flex items-center gap-3 rounded-2xl px-3 py-2.5 transition hover:bg-ds-mist/40"
      style={{ marginLeft: depth * 28 }}
    >
      {/* El conector de la rama, como el árbol del viejo. */}
      {depth > 0 && <span className="h-8 w-px shrink-0 bg-ds-border" aria-hidden />}
      {hasKids ? (
        <button
          type="button"
          aria-label={open ? t("Plegar la rama") : t("Abrir la rama")}
          aria-expanded={open}
          onClick={onToggle}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      ) : (
        <span className="w-6 shrink-0" aria-hidden />
      )}
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ds-lavender-bg-2 text-[12px] font-bold text-ds-violet-pop">
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13.5px] font-medium text-ds-ink">{member.displayName}</p>
        <p className="font-mono text-[11.5px] text-ds-ink-3">
          {formatReferralCode(member.referralCode)}
        </p>
      </div>
      <Badge variant={member.customerType === "distributor" ? "info" : "neutral"} size="sm">
        {t(CUSTOMER_TYPE_LABELS[member.customerType])}
      </Badge>
      <span className="text-[12px] text-ds-ink-3">{t("Nivel {{n}}", { n: member.level })}</span>
      <span className="hidden text-[12px] text-ds-ink-3 sm:inline">
        {formatOrderDate(member.enrolledAt)}
      </span>
      {/*
        ── Qué ha comprado, y qué me generó (Anahí, 29-ago) ────────────
        Plegado por defecto: la red se lee de un vistazo y el detalle se
        pide. Abrir a todos de golpe pediría una consulta por persona
        para enseñar tablas que casi nadie mira a la vez.
      */}
      <button
        type="button"
        aria-expanded={showActivity}
        aria-label={t("Ver lo que ha comprado {{nombre}}", { nombre: member.displayName })}
        onClick={() => setShowActivity((current) => !current)}
        className="shrink-0 rounded-lg p-1.5 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-violet-pop"
      >
        <Receipt className="h-4 w-4" />
      </button>
    </div>
    {showActivity && (
      <div className="mb-2 mt-1" style={{ marginLeft: depth * 28 + 40 }}>
        <MemberActivity userId={member.userId} />
      </div>
    )}
    </>
  );
}
